/**
 * Multi-reference i2v scenes, read from the delivery envelope (DCR-002).
 *
 * The orchestrator's cast lanes (people via Rekognition grouping, dogs via the
 * GPT census cast) stamp qualifying scenes with `identity_reference_files` and
 * an `identity_reference_intent` receipt; the composer crops those photos and
 * sends them to H3's reference-to-video graph so the clip keeps the subject's
 * identity. This module is the read side: which scenes went multi-reference,
 * who each reference belongs to, and which uploaded photos were used — joined
 * to media rows so the UI can show the actual pictures.
 *
 * Pure and tolerant: the envelope is operator-facing JSON from several
 * generations of orchestrator, so every shape it does not understand yields
 * nothing rather than throwing. Intent v2 carries `subjects[]` (kind
 * "person"/"dog", descriptor, per-reference file+box); v1 envelopes predate
 * subjects and describe exactly one person via flat fields.
 */

import type { EchoflicksSessionMedia } from "../types";
import {
  parseLegacyMouthLock,
  parseMouthLockV2,
  type LegacyMouthLock,
  type MouthLockV2,
} from "./mouthLock";
import { getDirectorScript, type ScriptEnvelope } from "./scriptStory";

export type IdentityReferenceEntry = {
  file: string;
  /** The uploaded photo, when the session media listing still carries it. */
  media?: EchoflicksSessionMedia;
};

export type IdentityReferenceSubject = {
  kind: "person" | "dog";
  /** Cast prose ("A young man with dark hair", "border collie: white chest"). */
  descriptor: string | null;
  groupId: string | null;
  references: IdentityReferenceEntry[];
  /** Intent v5+ person lock (`closed | as_source`); absent when not carried. */
  mouthLock?: LegacyMouthLock;
  /** Intent v6 per-scene lock; absent on older intents. */
  mouthLockV2?: MouthLockV2;
};

export type IdentityReferenceScene = {
  sceneId: string | number | null;
  /** Stable string key for joining against diagnostics keyed by scene id. */
  sceneKey: string | null;
  sourceFile: string | null;
  /** "cite" when the prompt names each reference (<Picture N>); "off" otherwise. */
  citationMode: string;
  subjects: IdentityReferenceSubject[];
  referenceCount: number;
};

function baseName(value: string): string {
  return value.split(/[\\/]/).at(-1) ?? value;
}

/**
 * The identity references for one timeline scene, matched by scene id the way
 * `lookupSceneAudit` matches manifest audits.
 *
 * Deliberately id-only: these scenes are a sparse subset of the timeline, so a
 * positional fallback would attribute one scene's references to another. A
 * scene the envelope never gave an id to simply shows no badge.
 */
export function lookupIdentityReferenceScene(
  scenes: IdentityReferenceScene[],
  scene: { sceneId?: number | string | null } | null,
): IdentityReferenceScene | null {
  const sceneId = scene?.sceneId;
  if (sceneId == null) return null;
  const key = String(sceneId);
  return scenes.find((candidate) => candidate.sceneKey === key) ?? null;
}

function mediaByFileName(
  media: EchoflicksSessionMedia[],
): Map<string, EchoflicksSessionMedia> {
  const byName = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const name =
      item.s3_key?.split("/").at(-1) ?? item.local_path?.split(/[\\/]/).at(-1);
    if (name && !byName.has(name)) byName.set(name, item);
  }
  return byName;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function referencesOf(
  raw: unknown,
  byName: Map<string, EchoflicksSessionMedia>,
): IdentityReferenceEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: IdentityReferenceEntry[] = [];
  for (const item of raw) {
    const record = asRecord(item);
    const file =
      typeof record?.file === "string"
        ? record.file
        : typeof item === "string"
          ? item
          : null;
    if (!file || !file.trim()) continue;
    entries.push({ file, media: byName.get(baseName(file)) });
  }
  return entries;
}

function subjectsOf(
  intent: Record<string, unknown> | null,
  files: string[],
  byName: Map<string, EchoflicksSessionMedia>,
): IdentityReferenceSubject[] {
  const rawSubjects = intent?.subjects;
  if (Array.isArray(rawSubjects) && rawSubjects.length > 0) {
    const subjects: IdentityReferenceSubject[] = [];
    for (const raw of rawSubjects) {
      const subject = asRecord(raw);
      if (!subject) continue;
      const references = referencesOf(subject.references, byName);
      if (references.length === 0) continue;
      const kind = subject.kind === "dog" ? "dog" : "person";
      // Mouth locks are person-only; a dog subject never carries one.
      const mouthLock =
        kind === "person" ? parseLegacyMouthLock(subject.mouth_lock) : null;
      const mouthLockV2 =
        kind === "person" ? parseMouthLockV2(subject.mouth_lock_v2) : null;
      subjects.push({
        kind,
        descriptor:
          typeof subject.descriptor === "string" && subject.descriptor.trim()
            ? subject.descriptor
            : null,
        groupId: typeof subject.group_id === "string" ? subject.group_id : null,
        references,
        ...(mouthLock ? { mouthLock } : {}),
        ...(mouthLockV2 ? { mouthLockV2 } : {}),
      });
    }
    return subjects;
  }
  // v1 intent: exactly one person, flat fields, references named only by the
  // scene's file list.
  if (files.length === 0) return [];
  return [
    {
      kind: "person",
      descriptor: null,
      groupId: typeof intent?.group_id === "string" ? intent.group_id : null,
      references: referencesOf(files, byName),
    },
  ];
}

/**
 * Every scene that carries identity references, in script order. Empty when
 * the lane never fired for this echo — the badge simply does not render.
 */
export function buildIdentityReferenceScenes(
  envelope: Record<string, unknown> | undefined,
  media: EchoflicksSessionMedia[],
): IdentityReferenceScene[] {
  if (!envelope) return [];
  const script = getDirectorScript(envelope as ScriptEnvelope);
  const scenes = Array.isArray(script?.video_content)
    ? script.video_content
    : [];
  const byName = mediaByFileName(media);
  const out: IdentityReferenceScene[] = [];
  for (const scene of scenes) {
    const record = asRecord(scene);
    const prompt = asRecord(record?.video_prompt);
    if (!prompt) continue;
    const files = Array.isArray(prompt.identity_reference_files)
      ? prompt.identity_reference_files.filter(
          (item): item is string => typeof item === "string" && !!item.trim(),
        )
      : [];
    if (files.length === 0) continue;
    const intent = asRecord(prompt.identity_reference_intent);
    const subjects = subjectsOf(intent, files, byName);
    if (subjects.length === 0) continue;
    const sceneId =
      typeof record?.id === "string" || typeof record?.id === "number"
        ? record.id
        : null;
    out.push({
      sceneId,
      sceneKey: sceneId == null ? null : String(sceneId),
      sourceFile: typeof prompt.file === "string" ? prompt.file : null,
      citationMode:
        typeof intent?.citation_mode === "string"
          ? intent.citation_mode
          : "off",
      subjects,
      referenceCount: subjects.reduce(
        (sum, subject) => sum + subject.references.length,
        0,
      ),
    });
  }
  return out;
}
